import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { programApi, ProgramListParams } from '@/api/program';
import { Program } from '@/types';
import { useActionFeedback } from '@/hooks/useActionFeedback';

export const useProgramList = (params: ProgramListParams = {}) => {
  return useQuery({
    queryKey: ['program', params],
    queryFn: () => programApi.getAll(params),
  });
};

export const useProgram = (id: number) => {
  return useQuery({
    queryKey: ['program', id],
    queryFn: () => programApi.getById(id),
    enabled: !!id,
  });
};

export const useCreateProgram = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (data: Partial<Program>) => programApi.create(data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program'] });
      feedback.success('Program berhasil dibuat');
    },
    onError: (error) => {
      feedback.error('Gagal membuat program', error);
    },
  });
};

export const useUpdateProgram = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<Program> }) =>
      programApi.update(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program'] });
      feedback.success('Program berhasil diperbarui');
    },
    onError: (error) => {
      feedback.error('Gagal memperbarui program', error);
    },
  });
};

export const useDeleteProgram = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (id: number) => programApi.delete(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program'] });
      feedback.success('Program berhasil dihapus');
    },
    onError: (error) => {
      feedback.error('Gagal menghapus program', error);
    },
  });
};

export const useToggleProgramStatus = () => {
  const queryClient = useQueryClient();
  const feedback = useActionFeedback();

  return useMutation({
    mutationFn: (id: number) => programApi.toggleStatus(id),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['program'] });
      feedback.success('Status program berhasil diubah');
    },
    onError: (error) => {
      feedback.error('Gagal mengubah status program', error);
    },
  });
};
